import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import { track } from "../lib/mixpanel"
import { filmedSecondsAt, videoTimeFor, walkPace, indexAtTime, indexNear, fanPath, fixShift, fixedVideoTime } from "../lib/site_walk_route"
import { showToast } from "../lib/toast"
import { rotatedImage } from "../lib/rotated_image_layer"
import { trackAt, videoTimeAt } from "../lib/level"
const MAP_MAX_ZOOM = 22
// How close to an edge the dot may get before the map glides along (share of the map's size).
const FOLLOW_MARGIN = 0.15

// The map beside a site walk's video (Phelipe 2026-09-25: "if the user
// uploaded the .insv, show where they walked on the map"). A raw Insta360 file
// carries the phone's GPS track; the server keeps it as the walk's `route`
// ([[seconds since filming started, lat, lon], ...]). The walk is drawn on
// satellite imagery, a dot follows the video, and a click on or near the path
// walks from that spot - the way DroneDeploy's mini-map works.
//
// Opened and closed by the Site Walks viewer (site_walks_controller.js
// dispatches site-walks:opened / site-walks:closed on window); a click asks it
// to move the video with site-walk-map:jump. Hidden for walks with no track.
const IMAGERY = "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
const PLACES = "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
const PATH_COLOR = "#fb7185" // light pink/red (rose-400), the same as the line on the ground in the 360° view
// How far from the path (in screen pixels) a click still counts as "on" it.
const CLICK_PIXELS = 40
// The fan showing which way the 360° view looks (#5): its length in screen
// pixels, and a blue that reads on satellite and against the pink path.
const FAN_RADIUS = 46
const FAN_FILL = "#3b82f6"
// Fix the dot (pmfit K9-1): a click counts within this many seconds of where the
// dot is now, so a spot the walk passed twice picks the right pass.
const FIX_WINDOW_SECONDS = 180

export default class extends Controller {
  static targets = ["map", "opacityRow", "opacity", "opacityValue", "fixButton", "fixBar", "fixText", "resetDot"]
  static values = { projectId: Number, projectPhase: String, planLabel: String, text: Object }

  connect() {
    this._follow = this._follow.bind(this)
  }

  disconnect() {
    this.hide()
  }

  show(event) {
    const { walk, video } = event.detail || {}
    this.hide()
    const route = Array.isArray(walk?.route) && walk.route.length >= 2 ? walk.route : null
    if (!route || !video) return

    this.walk = walk
    this.route = route
    this.video = video
    this.pace = walkPace(walk, video.duration)
    this.element.classList.remove("hidden")
    this._draw()
    this._drawViews(walk.views)
    this._showReset()
    video.addEventListener("timeupdate", this._follow)
    video.addEventListener("seeked", this._follow)
    this._follow()
  }

  hide() {
    if (this.video) {
      this.video.removeEventListener("timeupdate", this._follow)
      this.video.removeEventListener("seeked", this._follow)
    }
    this._endFix()
    this.planToggle = null
    this.planLayer = null
    this.droneToggle = null
    this.droneLayer = null
    if (this.map) this.map.remove()
    this.map = null
    this.dot = null
    this.fan = null
    this.facing = null
    this.viewMarkers = null
    this.planLayer = null
    this.planToggle = null
    this._showOpacity(false)
    this.video = null
    this.route = null
    this.walk = null
    this.element.classList.add("hidden")
  }

  _draw() {
    const points = this.route.map(([, lat, lng]) => [lat, lng])
    // Two levels closer than the imagery goes (Phelipe 2026-10-21, K3-77): the site plan on top stays
    // readable when zoomed in on a lot; past level 19 the satellite picture is only stretched.
    const map = L.map(this.mapTarget, { zoomControl: true, attributionControl: true, maxZoom: MAP_MAX_ZOOM })
    map.attributionControl.setPrefix(false)
    L.tileLayer(IMAGERY, { maxZoom: MAP_MAX_ZOOM, maxNativeZoom: 19, attribution: "Imagery © Esri" }).addTo(map)
    L.tileLayer(PLACES, { maxZoom: MAP_MAX_ZOOM, maxNativeZoom: 19, opacity: 0.85 }).addTo(map)
    // The project's site plan, lined up on the map (#16, #18), under the path.
    map.createPane("sitePlan").style.zIndex = 350

    // A white edge under the orange line so the path reads on any ground.
    L.polyline(points, { color: "#ffffff", weight: 7, opacity: 0.9, interactive: false }).addTo(map)
    const path = L.polyline(points, { color: PATH_COLOR, weight: 4, opacity: 1, interactive: false }).addTo(map)
    L.circleMarker(points[0], { radius: 5, color: "#ffffff", weight: 2, fillColor: "#16a34a", fillOpacity: 1, interactive: false }).addTo(map)
    L.circleMarker(points[points.length - 1], { radius: 5, color: "#ffffff", weight: 2, fillColor: "#dc2626", fillOpacity: 1, interactive: false }).addTo(map)
    this.dot = L.circleMarker(points[0], { radius: 7, color: "#ffffff", weight: 3, fillColor: PATH_COLOR, fillOpacity: 1, interactive: false }).addTo(map)
    // The view's fan sits under the path and the dot (Leaflet's overlays are at 400).
    map.createPane("siteWalkFacing").style.zIndex = 390

    map.on("click", (e) => this._jump(e))
    const frame = () => map.fitBounds(path.getBounds(), { padding: [18, 18] })
    frame()
    this.map = map
    this._drawDrone()
    this._drawPlan()
    // The viewer is still being laid out when this runs: measure again once it is.
    requestAnimationFrame(() => {
      if (this.map !== map) return
      map.invalidateSize()
      frame()
    })
  }

  // The dot sits where the video is: the route point filmed closest to the
  // moment on screen. Kept in view if someone has zoomed in.
  _follow() {
    if (!this.map || !this.video || !this.dot) return
    // When on the route the frame on screen was taken: the camera's own record (#6), or the pace
    // (worked out again here: an ordinary video's length is only known once it has loaded).
    const t = this.video.currentTime || 0
    this.pace = walkPace(this.walk, this.video.duration)
    const seconds = this._routeSecondsAt(t)
    const at = indexAtTime(this.route, seconds)
    const point = this.route[at]
    if (!point) return
    const here = L.latLng(point[1], point[2])
    this.dot.setLatLng(here)
    this.fan?.setLatLng(here)
    // Keep the dot and its view fan well inside the map (Phelipe 2026-10-21, K3-77 follow-up): once the
    // dot is within 15% of an edge, glide it back to the middle - not only after it has left the map,
    // which at a close zoom left the dot and half the fan cut off at the edge. Not while someone is
    // dragging the map, and not again while a glide is still running.
    if (this.map.dragging?._draggable?._moving || this._gliding) return
    if (!this.map.getBounds().pad(-FOLLOW_MARGIN).contains(here)) {
      this._gliding = true
      this.map.once("moveend", () => { this._gliding = false })
      this.map.panTo(here, { animate: true, duration: 0.6, easeLinearity: 0.5 })
    }
  }

  // Which way the 360° view looks (site-walks:facing, #5): a see-through fan
  // from the dot, turned to the view's compass bearing and as wide as the
  // view. Gone when there's no look-around (the flat video) - detail null.
  face(event) {
    const facing = event.detail
    if (!this.map || !this.dot) return
    if (!facing || !Number.isFinite(facing.bearing)) {
      this.fan?.remove()
      this.fan = null
      this.facing = null
      return
    }
    if (!this.fan) {
      const size = FAN_RADIUS * 2
      const icon = L.divIcon({
        className: "",
        iconSize: [size, size],
        iconAnchor: [FAN_RADIUS, FAN_RADIUS],
        html: `<div data-fan style="width:${size}px;height:${size}px;transform-origin:50% 50%">` +
              `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" aria-hidden="true">` +
              `<path fill="${FAN_FILL}" fill-opacity="0.35" stroke="#ffffff" stroke-opacity="0.85" stroke-width="1.5" stroke-linejoin="round"/></svg></div>`
      })
      this.fan = L.marker(this.dot.getLatLng(), { icon, pane: "siteWalkFacing", interactive: false, keyboard: false }).addTo(this.map)
    }
    const element = this.fan.getElement()
    const turner = element?.querySelector("[data-fan]")
    const path = element?.querySelector("path")
    if (turner) turner.style.transform = `rotate(${facing.bearing.toFixed(1)}deg)`
    if (path && Math.abs((this.facing?.spread ?? -99) - facing.spread) >= 1) path.setAttribute("d", fanPath(facing.spread, FAN_RADIUS))
    this.facing = facing
  }

  // The site plan on the satellite (#16): the walk lands on the drawing the
  // team works from. A switch in the corner hides it to see the ground.
  _drawPlan() {
    this.planLayer?.remove()
    this.planLayer = null
    const plan = this.walk?.site_plan
    if (!this.map || !plan?.corners || !plan.image_url) {
      this.planToggle?.remove()
      this.planToggle = null
      this._showOpacity(false)
      return
    }
    const opacity = this._storedOpacity(plan) ?? (plan.opacity || 0.7)
    this.planLayer = rotatedImage(L, plan.image_url, plan.corners, {
      width: plan.width, height: plan.height, opacity, pane: "sitePlan"
    }).addTo(this.map)
    this._showOpacity(true, opacity)
    if (!this.planToggle) {
      const label = this.planLabelValue || "Site plan"
      const Toggle = L.Control.extend({
        onAdd: () => {
          const button = L.DomUtil.create("button")
          button.type = "button"
          button.textContent = label
          button.style.cssText = "padding:3px 10px;border-radius:9999px;border:1px solid #d1d5db;background:#ffffff;" +
            "font:600 13px sans-serif;color:#111827;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.2)"
          button.setAttribute("aria-pressed", "true")
          L.DomEvent.disableClickPropagation(button)
          button.addEventListener("click", () => {
            const on = button.getAttribute("aria-pressed") !== "true"
            button.setAttribute("aria-pressed", String(on))
            button.style.opacity = on ? "1" : "0.6"
            if (this.hasOpacityTarget) this.opacityTarget.disabled = !on
            if (!this.planLayer) return
            if (on) this.planLayer.addTo(this.map)
            else this.planLayer.remove()
          })
          return button
        }
      })
      this.planToggle = new Toggle({ position: "topright" }).addTo(this.map)
    }
  }

  // The drone flight nearest the walk's day (pmfit K8-35) under the plan: the
  // site as the drone saw it that week, instead of the satellite's old picture.
  // A pill in the corner, beside the plan's, hides it.
  _drawDrone() {
    this.droneLayer?.remove()
    this.droneLayer = null
    const flight = this.walk?.drone_flight
    if (!this.map || !flight?.corners || !flight.image_url) {
      this.droneToggle?.remove()
      this.droneToggle = null
      return
    }
    this.map.getPane("droneMap") || (this.map.createPane("droneMap").style.zIndex = 340)
    this.droneLayer = rotatedImage(L, flight.image_url, flight.corners, { width: flight.width, height: flight.height, opacity: 1, pane: "droneMap" }).addTo(this.map)
    if (!this.droneToggle) {
      const label = `${this._t("drone_map", "Drone map")} · ${this._droneDay(flight)}`
      const Toggle = L.Control.extend({
        onAdd: () => {
          const button = L.DomUtil.create("button")
          button.type = "button"
          button.textContent = label
          button.style.cssText = "padding:3px 10px;border-radius:9999px;border:1px solid #d1d5db;background:#ffffff;" +
            "font:600 13px sans-serif;color:#111827;cursor:pointer;box-shadow:0 1px 2px rgba(0,0,0,.2)"
          button.setAttribute("aria-pressed", "true")
          L.DomEvent.disableClickPropagation(button)
          button.addEventListener("click", () => {
            const on = button.getAttribute("aria-pressed") !== "true"
            button.setAttribute("aria-pressed", String(on))
            button.style.opacity = on ? "1" : "0.6"
            if (!this.droneLayer) return
            if (on) this.droneLayer.addTo(this.map)
            else this.droneLayer.remove()
          })
          return button
        }
      })
      this.droneToggle = new Toggle({ position: "topright" }).addTo(this.map)
    }
  }

  _droneDay(flight) {
    const [y, m, d] = String(flight.flown_on || "").split("-").map(Number)
    if (!y) return ""
    return new Date(y, m - 1, d).toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric" })
  }

  // A plan lined up (site-plan:saved): show it on the open walk's map.
  planSaved(event) {
    const plan = event.detail?.plan
    if (!this.walk || !plan?.corners) return
    this.walk.site_plan = plan
    this._forgetOpacity(plan)
    this._drawPlan()
  }

  // The see-through slider under the map (K6-5, Larry 2026-09-29: "have the
  // ability to adjust your opacity right in here"). It changes the plan on the
  // map at once, and this browser remembers the level for that plan; the
  // level saved in the Site plan setup stays everyone's starting point.
  setOpacity(event) {
    const value = Math.min(1, Math.max(0.2, Number(event.target.value) || 0.7))
    this.planLayer?.setOpacity(value)
    this._labelOpacity(value)
    const plan = this.walk?.site_plan
    if (!plan?.id) return
    try { localStorage.setItem(this._opacityKey(plan), String(value)) } catch (_e) { /* private window: just not remembered */ }
  }

  opacityChanged(event) {
    track("Site Walk Plan See-through Changed", {
      project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
      site_walk_id: this.walk?.id, see_through: Math.round(Number(event.target.value) * 100)
    })
  }

  _showOpacity(on, value) {
    if (!this.hasOpacityRowTarget) return
    this.opacityRowTarget.hidden = !on
    if (!on) return
    this.opacityTarget.disabled = false
    this.opacityTarget.value = String(value)
    this._labelOpacity(value)
  }

  _labelOpacity(value) {
    if (this.hasOpacityValueTarget) this.opacityValueTarget.textContent = `${Math.round(value * 100)}%`
  }

  _opacityKey(plan) {
    return `siteWalkPlanOpacity:${plan.id}`
  }

  _storedOpacity(plan) {
    if (!plan?.id) return null
    try {
      const value = Number(localStorage.getItem(this._opacityKey(plan)))
      return value >= 0.2 && value <= 1 ? value : null
    } catch (_e) {
      return null
    }
  }

  _forgetOpacity(plan) {
    if (!plan?.id) return
    try { localStorage.removeItem(this._opacityKey(plan)) } catch (_e) { /* nothing kept */ }
  }

  // The walk's saved views (site-walks:views, Site Walks list #8/#14): small
  // pins where each was taken - orange for one that became an action item -
  // and a click goes back to that moment.
  views(event) {
    if (!this.map) return
    this._drawViews(event.detail?.views)
  }

  _drawViews(views) {
    this.viewMarkers?.remove()
    this.viewMarkers = null
    const placed = (views || []).filter((v) => Number.isFinite(v.latitude) && Number.isFinite(v.longitude))
    if (!this.map || !placed.length) return
    this.viewMarkers = L.layerGroup(placed.map((view) => {
      const marker = L.circleMarker([view.latitude, view.longitude], {
        radius: 6, color: "#ffffff", weight: 2, fillColor: view.action_item ? "#f97316" : "#2563eb", fillOpacity: 1
      })
      marker.bindTooltip(view.caption || view.action_item?.title || "", { direction: "top", offset: [0, -6] })
      marker.on("click", (e) => {
        L.DomEvent.stopPropagation(e)
        this.dispatch("jump", { detail: { at: view.at_seconds, yaw: view.yaw, pitch: view.pitch } })
      })
      return marker
    })).addTo(this.map)
  }

  // ── Fix the dot (pmfit K9-1, Phelipe 2026-10-17) ─────────────────────
  // The walk's own timing (the camera's record or the pace), then its fixes.
  _baseAt(t) {
    return trackAt(this.walk?.level_track, t, this.pace.frameSeconds)?.route ?? filmedSecondsAt(t, this.pace)
  }

  _routeSecondsAt(t) {
    return this._baseAt(t) + fixShift(t, this.walk?.route_fixes, (v) => this._baseAt(v))
  }

  _videoTimeFor(seconds) {
    const own = (s) => videoTimeAt(this.walk?.level_track, s, this.pace.frameSeconds) ??
      videoTimeFor(s, { ...this.pace, duration: this.video?.duration })
    return fixedVideoTime(seconds, this.walk?.route_fixes, (v) => this._baseAt(v), own)
  }

  _t(key, fallback) {
    return this.textValue?.[key] || fallback
  }

  // "Fix the dot": the video pauses where the person knows the spot, and the
  // next click on the map says where the camera really was.
  startFix() {
    if (!this.map || !this.video || !this.hasFixBarTarget) return
    this.video.pause()
    this.fixing = true
    this.fixTextTarget.textContent = this._t("prompt", "The video is paused. Click on the map where the camera really is right now.")
    this.fixBarTarget.classList.remove("hidden")
    this.mapTarget.style.cursor = "crosshair"
    if (this.hasFixButtonTarget) this.fixButtonTarget.disabled = true
  }

  cancelFix() {
    this._endFix()
  }

  _endFix() {
    this.fixing = false
    if (this.hasFixBarTarget) this.fixBarTarget.classList.add("hidden")
    if (this.hasMapTarget) this.mapTarget.style.cursor = ""
    if (this.hasFixButtonTarget) this.fixButtonTarget.disabled = false
  }

  async _fixAt(event) {
    const t = this.video.currentTime || 0
    const now = this._routeSecondsAt(t)
    let best = -1
    let bestPixels = Infinity
    this.route.forEach((p, i) => {
      if (Math.abs(p[0] - now) > FIX_WINDOW_SECONDS) return
      const px = this.map.latLngToContainerPoint([p[1], p[2]]).distanceTo(event.containerPoint)
      if (px < bestPixels) { bestPixels = px; best = i }
    })
    if (best < 0 || bestPixels > CLICK_PIXELS * 2) {
      this.fixTextTarget.textContent = this._t("bad", "Click on or near the path, close to where the dot is.")
      return
    }
    const data = await this._send("PATCH", { video_seconds: t, route_seconds: this.route[best][0] })
    if (!data) return
    this.walk.route_fixes = data.route_fixes || []
    this._endFix()
    this._follow()
    this._showReset()
    showToast(this._t("saved", "Dot lined up. The rest of this walk moves with it."))
    track("Site Walk Dot Fixed", {
      project_id: this.projectIdValue, project_phase: this.projectPhaseValue, site_walk_id: this.walk?.id,
      fixes: this.walk.route_fixes.length, timelapse: Boolean(this.walk?.timelapse)
    })
  }

  async resetDot() {
    const data = await this._send("DELETE")
    if (!data || !this.walk) return
    this.walk.route_fixes = []
    this._follow()
    this._showReset()
    showToast(this._t("reset_done", "The dot is back to how the walk was filmed."))
  }

  _showReset() {
    if (this.hasResetDotTarget) this.resetDotTarget.classList.toggle("hidden", !(this.walk?.route_fixes || []).length)
  }

  async _send(method, body) {
    if (!this.walk?.id || !this.projectIdValue) return null
    try {
      const res = await fetch(`/projects/${this.projectIdValue}/site_walks/${this.walk.id}/dot`, {
        method, credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: body ? JSON.stringify(body) : undefined
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) return data
      showToast(data.error || this._t("failed", "Couldn't save. Try again."), "error")
    } catch (_) {
      showToast(this._t("failed", "Couldn't save. Try again."), "error")
    }
    return null
  }

  // A click on or near the path: the video walks from that spot (or, while
  // fixing the dot, says where the camera really was).
  _jump(event) {
    if (!this.map || !this.route) return
    if (this.fixing) return this._fixAt(event)
    const i = indexNear(this.route, event.latlng.lat, event.latlng.lng)
    if (i < 0) return
    const spot = this.map.latLngToContainerPoint([this.route[i][1], this.route[i][2]])
    if (spot.distanceTo(event.containerPoint) > CLICK_PIXELS) return

    const at = this._videoTimeFor(this.route[i][0])
    this.dispatch("jump", { detail: { at } })
    track("Site Walk Map Jumped", {
      project_id: this.projectIdValue, project_phase: this.projectPhaseValue,
      site_walk_id: this.walk?.id, timelapse: Boolean(this.walk?.timelapse)
    })
  }
}
